import React, { useCallback, useEffect, useState } from 'react';
import { useOrdenTabla, ThOrdenable } from '../utils/ordenarTabla';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getQueueStats } from '../api';
import { formatNumber } from '../utils/format';
import DownloadButton from '../components/DownloadButton';
import AnalyticsFilterBar from '../components/AnalyticsFilterBar';
import { usePeticionVigente } from '../utils/peticionVigente';

export default function Queues() {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  // `null` hasta que la barra emita: el estado inicial sin fechas consultaba
  // todo el histórico, y esa respuesta llegaba después y pisaba la filtrada.
  // Ver `utils/peticionVigente.js`.
  const [filters, setFilters] = useState(null);
  const nuevaPeticion = usePeticionVigente();
  // Tabla completa (no paginada): ordenar en el cliente es correcto aquí.
  const orden = useOrdenTabla(queues, {
    queue_name: (q) => q.queue_name,
    total_calls: (q) => q.total_calls,
    avg_duration_seconds: (q) => q.avg_duration_seconds,
    positive: (q) => q.sentiment_distribution?.positive || 0,
    neutral: (q) => q.sentiment_distribution?.neutral || 0,
    negative: (q) => q.sentiment_distribution?.negative || 0,
  }, 'total_calls');

  const fetchAll = useCallback((params) => {
    const vigente = nuevaPeticion();
    setLoading(true);
    getQueueStats(params)
      .then(r => { if (vigente()) setQueues(r.data); })
      .catch(() => {})
      .finally(() => { if (vigente()) setLoading(false); });
  }, [nuevaPeticion]);

  useEffect(() => { if (filters) fetchAll(filters); }, [filters, fetchAll]);

  // La barra va SIEMPRE en la misma posición (fuera del condicional de carga)
  // para no remontarla al alternar loading (si no, pierde estado y re-emite →
  // loop de carga). chartData deriva de queues (init []), seguro en carga.
  const chartData = queues.map(q => ({
    name: q.queue_name?.replace('V_CRC_', '').replace('V_', '') || 'N/A',
    calls: q.total_calls,
    avgDuration: Math.round(q.avg_duration_seconds),
  }));

  return (
    <div>
      <h1 className="page-title">Colas</h1>
      <p className="page-subtitle">{formatNumber(queues.length)} colas con llamadas procesadas</p>
      <AnalyticsFilterBar onChange={setFilters} />

      {loading ? (
        <div className="loading">Cargando colas...</div>
      ) : (
        <>
      <div className="charts-grid">
        <div className="card">
          <div className="card-title">Volumen por cola</div>
          <ResponsiveContainer width="100%" height={Math.max(250, queues.length * 35)}>
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10 }} width={160} />
              <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
              <Bar dataKey="calls" fill="#818cf8" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-title">Duracion promedio por cola (seg)</div>
          <ResponsiveContainer width="100%" height={Math.max(250, queues.length * 35)}>
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10 }} width={160} />
              <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
              <Bar dataKey="avgDuration" fill="#6ee7b7" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20, marginBottom: 8 }}>
        <DownloadButton
          columns={[
            { key: 'queue_name', label: 'Cola' },
            { key: 'total_calls', label: 'Llamadas' },
            { key: 'avg_duration_seconds', label: 'Dur. prom. (s)' },
            { key: 'positive', label: 'Positivo' },
            { key: 'neutral', label: 'Neutral' },
            { key: 'negative', label: 'Negativo' },
          ]}
          rows={queues.map(q => ({
            queue_name: q.queue_name,
            total_calls: q.total_calls,
            avg_duration_seconds: Math.round(q.avg_duration_seconds),
            positive: q.sentiment_distribution.positive || 0,
            neutral: q.sentiment_distribution.neutral || 0,
            negative: q.sentiment_distribution.negative || 0,
          }))}
          filename="colas"
        />
      </div>

      <div className="card" style={{ marginTop: 0, padding: 0, overflow: 'hidden' }}>
        <table className="data-table">
          <thead>
            <tr>
              <ThOrdenable columna="queue_name" orden={orden}>Cola</ThOrdenable>
              <ThOrdenable columna="total_calls" orden={orden}>Llamadas</ThOrdenable>
              <ThOrdenable columna="avg_duration_seconds" orden={orden}>Dur. prom.</ThOrdenable>
              <ThOrdenable columna="positive" orden={orden}>Positivo</ThOrdenable>
              <ThOrdenable columna="neutral" orden={orden}>Neutral</ThOrdenable>
              <ThOrdenable columna="negative" orden={orden}>Negativo</ThOrdenable>
            </tr>
          </thead>
          <tbody>
            {orden.filas.map(q => (
              <tr key={q.queue_name}>
                <td style={{ fontWeight: 600, fontSize: 12 }}><Link to={`/queues/${encodeURIComponent(q.queue_name)}`} style={{ color: '#a5b4fc', textDecoration: 'none' }}>{q.queue_name}</Link></td>
                <td>{formatNumber(q.total_calls)}</td>
                <td>{Math.round(q.avg_duration_seconds)}s</td>
                <td><span className="badge badge-positive">{formatNumber(q.sentiment_distribution.positive || 0)}</span></td>
                <td><span className="badge badge-neutral">{formatNumber(q.sentiment_distribution.neutral || 0)}</span></td>
                <td><span className="badge badge-negative">{formatNumber(q.sentiment_distribution.negative || 0)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
}
